import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";

const IMG_META = [
  {
    src: "https://images.unsplash.com/photo-1632200823229-376320621350?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    span: "md:col-span-7 md:row-span-2",
    ratio: "aspect-[4/5] md:aspect-auto md:h-full",
  },
  {
    src: "https://images.unsplash.com/photo-1644346402970-d8d7304ff82f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    span: "md:col-span-5",
    ratio: "aspect-[4/3]",
  },
  {
    src: "https://images.unsplash.com/photo-1521140714186-512186ea3d3b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    span: "md:col-span-5",
    ratio: "aspect-[4/3]",
  },
];

export default function Gallery() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ background: "var(--ink)" }}
      data-testid="gallery-section"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16 text-[var(--cream)]">
          <div>
            <div className="chip-num mb-4">
              <span style={{ color: "var(--marigold-2)" }}>{t.gallery.chip}</span>
            </div>
            <h2 className="font-display font-medium text-[2.4rem] sm:text-[3rem] lg:text-[3.8rem] leading-[1] tracking-[-0.03em]">
              {t.gallery.h2_a}{" "}
              <span className="font-editorial-italic text-[var(--marigold-2)]">
                {t.gallery.h2_italic}
              </span>
            </h2>
          </div>
          <p className="max-w-sm text-[var(--cream)]/60 text-[15px] leading-relaxed">
            {t.gallery.p}
          </p>
        </div>

        <motion.div
          style={{ y }}
          className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4"
        >
          {t.gallery.images.map((img, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.8,
                delay: i * 0.1,
                ease: [0.2, 0.8, 0.2, 1],
              }}
              className={`relative rounded-[22px] overflow-hidden ${IMG_META[i].span} ${IMG_META[i].ratio} group`}
            >
              <img
                src={IMG_META[i].src}
                alt={img.label}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[rgba(15,31,23,0.85)] to-transparent" />
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[var(--marigold)]" />
                <span className="font-mono-caption text-[10px] tracking-[0.22em] uppercase text-[var(--cream)]/90">
                  {img.label}
                </span>
              </div>
              <figcaption className="absolute left-5 bottom-5 right-5 text-[var(--cream)]">
                <div className="font-display text-xl md:text-2xl leading-snug tracking-tight">
                  {img.caption}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
